import { useEffect, useMemo } from 'react';

import uniqBy from 'lodash/uniqBy';

import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import type { DecisionMakerTargetedPetitionsItem } from 'src/app/pages/decisionMakerProfile/pageContext';
import {
	useDecisionMakerProfilePageData,
	useDMProfilePagePaginatedResponsesData,
	useDMProfilePagePaginatedTargetedPetitionsData,
} from 'src/app/pages/decisionMakerProfile/pageContext';
import { useTrackOnce } from 'src/app/shared/hooks/tracking';

type Result = ModelHookResult<
	{
		petitions: readonly DecisionMakerTargetedPetitionsItem[];
		hasNextPage: boolean;
		nextPageLoading: boolean;
		sectionHeader: string;
	},
	{
		onClickPetitionLink: (petitionId: string) => void;
		loadMore: () => void;
	}
>;

export function useTargetedPetitionList(): Result {
	const {
		data: { items, hasNextPage, nextPageLoading, totalCount: petitionsCount },
		actions: { loadMore },
	} = useDMProfilePagePaginatedTargetedPetitionsData();

	const {
		data: { totalCount: responsesCount },
	} = useDMProfilePagePaginatedResponsesData();

	const {
		data: { displayName },
	} = useDecisionMakerProfilePageData();

	const track = useTracking();
	const { translate, translatePlural } = useI18n();

	const onClickPetitionLink = (petitionId: string) => {
		void track('dm_profile_petition_click', { petition_id: petitionId });
	};

	// checks for responsesCount because its value deteremines
	// whether or not we show the statistics section
	const showPetitionCountInHeader = !responsesCount && !!petitionsCount;
	const sectionHeader = showPetitionCountInHeader
		? translatePlural('corgi.decision_maker_profile.petitions_list.header_with_count', petitionsCount, {
				count: petitionsCount,
				displayName,
			})
		: translate('corgi.decision_maker_profile.petitions_list.header', { displayName });

	// Copied from useTargetedPetitionList:
	// some petitions are sometimes present in duplicate in the list returned by the API...
	const petitions = useMemo(() => uniqBy(items, 'id'), [items]);
	const trackOnce = useTrackOnce();
	useEffect(() => {
		if (petitions.length > 0) void trackOnce('dm_profile_petition_list_view');
	}, [petitions.length, trackOnce]);

	return {
		data: {
			petitions,
			hasNextPage,
			nextPageLoading,
			sectionHeader,
		},
		actions: {
			onClickPetitionLink,
			loadMore,
		},
	};
}
